/**
 * The one formatting module for apps/app. A view never formats a number or a
 * date itself (no `toFixed`, `toLocaleString`, `Intl.*` outside this file).
 *
 * Rules (apps/app/DESIGN.md §Formatting):
 *  - An absent measurement is not zero: null, undefined, NaN and ±Infinity
 *    render as "—". Zero is a real measurement and formats like any value.
 *  - Choose the preset by SURFACE:
 *      `table`  – a dense column that must line up: full precision, no
 *                 abbreviation ($84,210.00, 5.00%, 10,000, 13.40).
 *      `glance` – a headline, card, chart tick or tooltip read in seconds:
 *                 abbreviated by magnitude, cents only under $100
 *                 ($84.2k, $6.51, 1.2M, 5.0%).
 *  - The unit comes from the API (currency | count | percent | ratio), never
 *    inferred from a metric name.
 *  - `percent` values arrive as 0..1 fractions. Percentage-POINT movements are
 *    `pp`, never `%`.
 *  - An unbounded ratio (IPM) is not a percentage.
 *  - Dates are UTC days; a rendered time always names its zone.
 *  - Render every comparable number inside an element with the `num` class
 *    (tabular figures).
 */

export const DASH = '—';
/** Real minus sign, so signed figures align with unsigned ones. */
const MINUS = '−';

export type MetricUnit = 'currency' | 'count' | 'percent' | 'ratio';
export type FormatPreset = 'table' | 'glance';

/** True when `value` is a finite number — i.e. a measurement exists. */
export const isMeasured = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const LOCALE = 'en-US';

const withSign = (negative: boolean, body: string): string => (negative ? `${MINUS}${body}` : body);

const fixed = (value: number, decimals: number, grouping = true): string =>
  new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: grouping,
  }).format(value);

/** 12.4k / 1.2M above 10,000; plain grouped digits below. */
const abbreviate = (magnitude: number, decimals: number): string => {
  if (magnitude >= 1_000_000_000) return `${fixed(magnitude / 1_000_000_000, 1)}B`;
  if (magnitude >= 1_000_000) return `${fixed(magnitude / 1_000_000, 1)}M`;
  if (magnitude >= 10_000) return `${fixed(magnitude / 1_000, 1)}k`;
  return fixed(magnitude, decimals);
};

// --- Numbers ---------------------------------------------------------------

/** A whole count with thousands separators: 10000 → "10,000". */
export const formatInt = (value: number | null | undefined): string => {
  if (!isMeasured(value)) return DASH;
  const rounded = Math.round(value);
  return withSign(rounded < 0, fixed(Math.abs(rounded), 0));
};

/**
 * A grouped number with UP TO `maxDecimals` fraction digits and no trailing
 * zeros: formatDecimal(0.002, 3) → "0.002", formatDecimal(12, 3) → "12",
 * formatDecimal(1234.5678, 3) → "1,234.568". For quantities priced finer
 * than whole units (attribute credits bill in milli-credits), where a whole
 * number would print "0" for a real charge.
 */
export const formatDecimal = (value: number | null | undefined, maxDecimals = 0): string => {
  if (!isMeasured(value)) return DASH;
  const body = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: maxDecimals }).format(Math.abs(value));
  return withSign(value < 0 && body !== '0', body);
};

/** A count abbreviated by magnitude: 12400 → "12.4k", 980 → "980". */
export const formatCompact = (value: number | null | undefined): string => {
  if (!isMeasured(value)) return DASH;
  return withSign(value < 0, abbreviate(Math.abs(value), 0));
};

/**
 * Money in major units (dollars).
 * glance: cents only under $100 ($6.51, $840, $84.2k).
 * table:  always two decimals with separators ($84,210.00).
 */
export const formatMoney = (
  value: number | null | undefined,
  preset: FormatPreset = 'glance',
): string => {
  if (!isMeasured(value)) return DASH;
  const magnitude = Math.abs(value);
  const body =
    preset === 'table'
      ? fixed(magnitude, 2)
      : magnitude < 100
        ? fixed(magnitude, 2)
        : abbreviate(magnitude, 0);
  return withSign(value < 0, `$${body}`);
};

/**
 * Money in minor units (cents), rendered without cents: 55800 → "$558".
 * Unknown currency codes fall back to "EUR 558".
 */
export const formatMoneyMinor = (
  minor: number | null | undefined,
  currency = 'USD',
): string => {
  if (!isMeasured(minor)) return DASH;
  const code = currency.toUpperCase();
  const major = minor / 100;
  try {
    const body = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(Math.abs(major));
    return withSign(major < 0, body);
  } catch {
    return withSign(major < 0, `${code} ${fixed(Math.abs(major), 0)}`);
  }
};

/**
 * A 0..1 fraction read as a percentage: 0.0523 → "5.2%".
 * Without `decimals`, tiny rates keep two decimals (0.0042 → "0.42%") so they
 * do not collapse to "0.0%".
 */
export const formatPercent = (
  fraction: number | null | undefined,
  decimals?: number,
): string => {
  if (!isMeasured(fraction)) return DASH;
  const pct = fraction * 100;
  const places = decimals ?? (Math.abs(fraction) < 0.01 && fraction !== 0 ? 2 : 1);
  return withSign(pct < 0, `${fixed(Math.abs(pct), places)}%`);
};

/** An unbounded ratio such as IPM: 13.4 → "13.40". Never scaled to %. */
export const formatRatio = (value: number | null | undefined, decimals = 2): string => {
  if (!isMeasured(value)) return DASH;
  return withSign(value < 0, fixed(Math.abs(value), decimals));
};

/** One entry point for API-described metrics. See the preset rules above. */
export const formatMetric = (
  value: number | null | undefined,
  unit: MetricUnit,
  preset: FormatPreset = 'glance',
): string => {
  switch (unit) {
    case 'currency':
      return formatMoney(value, preset);
    case 'count':
      return preset === 'table' ? formatInt(value) : formatCompact(value);
    case 'percent':
      return formatPercent(value, preset === 'table' ? 2 : undefined);
    case 'ratio':
      return formatRatio(value);
  }
};

// --- Deltas ----------------------------------------------------------------

const signed = (value: number, body: string): string =>
  value > 0 ? `+${body}` : value < 0 ? `${MINUS}${body}` : body;

/** A relative change given as a fraction: 0.124 → "+12%", −0.031 → "−3.1%". */
export const formatDeltaPct = (fraction: number | null | undefined): string => {
  if (!isMeasured(fraction)) return DASH;
  const magnitude = Math.abs(fraction * 100);
  const decimals = magnitude < 10 && magnitude !== 0 ? 1 : 0;
  return signed(fraction, `${fixed(magnitude, decimals)}%`);
};

/**
 * A movement in percentage POINTS, already in points: 3 → "+3.0pp".
 * A rate going 35% → 38% moved 3pp; calling that "+8.6%" is a different claim.
 */
export const formatDeltaPp = (points: number | null | undefined): string => {
  if (!isMeasured(points)) return DASH;
  return signed(points, `${fixed(Math.abs(points), 1)}pp`);
};

export type DeltaTone = 'good' | 'bad' | 'flat';

/**
 * Whether a movement reads as an improvement. Direction alone is not enough:
 * CPI falling is good, ROAS falling is not. Colour is never the only signal —
 * pair the tone with `deltaGlyph`.
 */
export const deltaTone = (
  delta: number | null | undefined,
  higherIsBetter: boolean | null,
): DeltaTone => {
  if (!isMeasured(delta) || delta === 0 || higherIsBetter === null) return 'flat';
  return (delta > 0) === higherIsBetter ? 'good' : 'bad';
};

/** ▲ for up, ▼ for down, nothing for flat or absent. Direction, not judgement. */
export const deltaGlyph = (delta: number | null | undefined): string => {
  if (!isMeasured(delta) || delta === 0) return '';
  return delta > 0 ? '▲' : '▼';
};

// --- Dates (UTC) -----------------------------------------------------------

/** Parse "YYYY-MM-DD" as a UTC day, anything else as an ISO instant. */
const parseDate = (value: string | Date | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const date =
    value instanceof Date
      ? value
      : new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const utc = (options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(LOCALE, { ...options, timeZone: 'UTC' });

/** "Sep 16, 2026" — a calendar day in UTC. */
export const formatUtcDate = (value: string | Date | null | undefined): string => {
  const date = parseDate(value);
  return date ? utc({ month: 'short', day: 'numeric', year: 'numeric' }).format(date) : DASH;
};

/** "Sep 16" — a day label for axes and compact ranges, in UTC. */
export const formatUtcDay = (value: string | Date | null | undefined): string => {
  const date = parseDate(value);
  return date ? utc({ month: 'short', day: 'numeric' }).format(date) : DASH;
};

/** "September 2026" — a calendar month in UTC (e.g. a month-grouped row). */
export const formatUtcMonth = (value: string | Date | null | undefined): string => {
  const date = parseDate(value);
  return date ? utc({ month: 'long', year: 'numeric' }).format(date) : DASH;
};

/** "Sep 16 – Sep 29, 2026" (or with both years when they differ), UTC days. */
export const formatUtcRange = (
  from: string | Date | null | undefined,
  to: string | Date | null | undefined,
): string => {
  const start = parseDate(from);
  const end = parseDate(to);
  if (!start || !end) return DASH;
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const left = sameYear ? formatUtcDay(start) : formatUtcDate(start);
  return `${left} – ${formatUtcDate(end)}`;
};

/** "Sep 16, 2026, 9:30 PM UTC" — an instant, always with the zone named. */
export const formatUtcDateTime = (value: string | Date | null | undefined): string => {
  const date = parseDate(value);
  if (!date) return DASH;
  return utc({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
};

/** "data 2h ago" — freshness in the coarsest honest unit. */
export const formatDataAge = (
  value: string | Date | null | undefined,
  now: Date = new Date(),
): string => {
  const date = parseDate(value);
  if (!date) return DASH;
  const hours = Math.floor((now.getTime() - date.getTime()) / 3_600_000);
  if (hours < 1) return 'data just now';
  if (hours < 24) return `data ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'data 1 day ago' : `data ${days} days ago`;
};

/**
 * "just now", "5 min ago", "3 h ago", "2 days ago" — how long ago the user did
 * something (a history entry). Older than 30 days (or in the future) it is the
 * UTC calendar day instead, because "94 days ago" is harder to place.
 */
export const formatTimeAgo = (
  value: string | Date | null | undefined,
  now: Date = new Date(),
): string => {
  const date = parseDate(value);
  if (!date) return DASH;
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 0) return formatUtcDate(date);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days > 30) return formatUtcDate(date);
  return days === 1 ? '1 day ago' : `${days} days ago`;
};

// --- Sizes -----------------------------------------------------------------

/** A file size in binary units, as file managers show it: "512 B", "820 KB", "12.4 MB". */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (!isMeasured(bytes) || bytes < 0) return DASH;
  if (bytes < 1024) return `${fixed(bytes, 0)} B`;
  if (bytes < 1024 * 1024) return `${fixed(bytes / 1024, 0)} KB`;
  return `${fixed(bytes / (1024 * 1024), 1)} MB`;
};

// --- Names -----------------------------------------------------------------

let regionNames: Intl.DisplayNames | null | undefined;

/**
 * A country as "Name (CC)": "United States (US)". Uses the name the API
 * supplied when it is a real name, else `Intl.DisplayNames`; an unknown code
 * is shown as is, never dropped (parity §0 "Country").
 */
export const formatCountry = (code: string, suppliedName?: string | null): string => {
  const upper = code.toUpperCase();
  let name = suppliedName && suppliedName !== code && suppliedName !== upper ? suppliedName : undefined;
  if (!name && /^[A-Z]{2}$/.test(upper)) {
    if (regionNames === undefined) {
      try {
        regionNames = new Intl.DisplayNames([LOCALE], { type: 'region' });
      } catch {
        regionNames = null;
      }
    }
    try {
      const resolved = regionNames?.of(upper);
      name = resolved && resolved !== upper ? resolved : undefined;
    } catch {
      name = undefined;
    }
  }
  return name ? `${name} (${upper})` : code;
};
